import React, { useEffect, useRef, useState, useCallback } from "react";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Play, Pause, Plus, Trash, FloppyDisk, Copy, FilmReel } from "@phosphor-icons/react";
import { isImageFrame } from "../../lib/gamecraft";

const GRID = 32;
const CELL = 12;
const PALETTE = ["#22d3ee", "#facc15", "#ef4444", "#84cc16", "#a78bfa", "#ffffff", "#000000"];

function emptyFrame() {
  return Array(GRID * GRID).fill(null);
}

export default function AnimationPanel({ project, onChange, importAnim }) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const [frames, setFrames] = useState([emptyFrame()]);
  const [current, setCurrent] = useState(0);
  const [color, setColor] = useState("#22d3ee");
  const [onionSkin, setOnionSkin] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [fps, setFps] = useState(8);
  const [drawing, setDrawing] = useState(false);
  const [name, setName] = useState("Walk");

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0a0a0c";
    ctx.fillRect(0, 0, c.width, c.height);

    for (let y = 0; y < GRID; y++) {
      for (let x = 0; x < GRID; x++) {
        if ((x + y) % 2 === 0) {
          ctx.fillStyle = "#111114";
          ctx.fillRect(x * CELL, y * CELL, CELL, CELL);
        }
      }
    }

    const paintPixels = (pixels, alpha) => {
      ctx.globalAlpha = alpha;
      for (let y = 0; y < GRID; y++) {
        for (let x = 0; x < GRID; x++) {
          const c2 = pixels[y * GRID + x];
          if (c2) { ctx.fillStyle = c2; ctx.fillRect(x * CELL, y * CELL, CELL, CELL); }
        }
      }
      ctx.globalAlpha = 1;
    };

    const f = frames[current];
    if (isImageFrame(f)) {
      const img = new Image();
      img.onload = () => {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0, c.width, c.height);
        ctx.strokeStyle = "rgba(255,255,255,0.04)";
        for (let i = 0; i <= GRID; i++) {
          ctx.beginPath(); ctx.moveTo(i * CELL + 0.5, 0); ctx.lineTo(i * CELL + 0.5, GRID * CELL); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(0, i * CELL + 0.5); ctx.lineTo(GRID * CELL, i * CELL + 0.5); ctx.stroke();
        }
      };
      img.src = f.data_url;
      return;
    }

    if (onionSkin && current > 0 && Array.isArray(frames[current - 1])) {
      paintPixels(frames[current - 1], 0.3);
    }
    if (Array.isArray(f)) paintPixels(f, 1);

    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    for (let i = 0; i <= GRID; i++) {
      ctx.beginPath(); ctx.moveTo(i * CELL + 0.5, 0); ctx.lineTo(i * CELL + 0.5, GRID * CELL); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i * CELL + 0.5); ctx.lineTo(GRID * CELL, i * CELL + 0.5); ctx.stroke();
    }
  }, [frames, current, onionSkin]);

  useEffect(() => { draw(); }, [draw]);

  const pixelAt = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / (rect.width / GRID));
    const y = Math.floor((e.clientY - rect.top) / (rect.height / GRID));
    if (x < 0 || y < 0 || x >= GRID || y >= GRID) return -1;
    return y * GRID + x;
  };

  const apply = (e) => {
    if (isImageFrame(frames[current])) return;
    const idx = pixelAt(e);
    if (idx < 0) return;
    setFrames((fs) => fs.map((f, i) => i === current && Array.isArray(f) ? f.map((c, k) => k === idx ? color : c) : f));
  };

  const cloneFrame = (f) => (isImageFrame(f) ? { ...f } : [...(Array.isArray(f) ? f : emptyFrame())]);

  const addFrame = () => {
    setFrames((fs) => [...fs.slice(0, current + 1), cloneFrame(fs[current]), ...fs.slice(current + 1)]);
    setCurrent((c) => c + 1);
  };
  const removeFrame = () => {
    if (frames.length <= 1) return;
    setFrames((fs) => fs.filter((_, i) => i !== current));
    setCurrent((c) => Math.max(0, c - 1));
  };
  const duplicateFrame = () => {
    setFrames((fs) => [...fs.slice(0, current + 1), cloneFrame(fs[current]), ...fs.slice(current + 1)]);
    setCurrent((c) => c + 1);
  };

  useEffect(() => {
    if (!importAnim?.frames?.length) return;
    setName(importAnim.name || "Walk");
    setFps(importAnim.fps || 8);
    setFrames(importAnim.frames);
    setCurrent(0);
    setPlaying(true);
  }, [importAnim]);

  // Play preview
  useEffect(() => {
    if (!playing) return;
    let raf;
    let lastTime = 0;
    let frameIdx = 0;
    const stepMs = 1000 / Math.max(1, fps);

    const loop = (ts) => {
      if (!lastTime) lastTime = ts;
      if (ts - lastTime >= stepMs) {
        frameIdx = (frameIdx + 1) % frames.length;
        lastTime = ts;
        renderPreview(frameIdx);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, frames, fps]);

  const renderPreview = (idx) => {
    const c = previewRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = "#0a0a0c";
    ctx.fillRect(0, 0, c.width, c.height);
    const f = frames[idx];
    if (isImageFrame(f)) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = f.data_url;
      return;
    }
    if (!Array.isArray(f)) return;
    for (let y = 0; y < GRID; y++) {
      for (let x = 0; x < GRID; x++) {
        const col = f[y * GRID + x];
        if (col) { ctx.fillStyle = col; ctx.fillRect(x * 4, y * 4, 4, 4); }
      }
    }
  };
  useEffect(() => { renderPreview(current); }, [current, frames]); // eslint-disable-line

  const handleSave = () => {
    const animations = project.animations || [];
    const anim = {
      id: crypto.randomUUID(),
      name: name || "anim",
      fps,
      grid: GRID,
      frames,
      created_at: new Date().toISOString(),
    };
    onChange({ animations: [...animations, anim] });
    toast.success(t("ide.saved"));
  };

  return (
    <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400 flex items-center gap-2">
            <FilmReel size={14} weight="duotone" /> ANIMATION TIMELINE
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 flex items-center gap-2">
              <input type="checkbox" data-testid="onion-skin-toggle" checked={onionSkin} onChange={(e) => setOnionSkin(e.target.checked)} className="accent-cyan-400" />
              Onion skin
            </label>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-3 inline-block">
          <canvas
            ref={canvasRef}
            width={GRID * CELL}
            height={GRID * CELL}
            data-testid="anim-canvas"
            onMouseDown={(e) => { setDrawing(true); apply(e); }}
            onMouseUp={() => setDrawing(false)}
            onMouseLeave={() => setDrawing(false)}
            onMouseMove={(e) => { if (drawing) apply(e); }}
            style={{ imageRendering: "pixelated", cursor: "crosshair" }}
          />
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="flex gap-px bg-zinc-800 border border-zinc-800">
            {PALETTE.map((c) => (
              <button
                key={c}
                data-testid={`anim-color-${c}`}
                onClick={() => setColor(c)}
                className={"w-7 h-7 " + (color === c ? "ring-2 ring-cyan-400" : "")}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-9 h-9 bg-transparent border border-zinc-800 cursor-pointer"
          />
        </div>

        {/* Timeline */}
        <div className="mt-6">
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-2">// TIMELINE — {frames.length} frames</div>
          <div className="flex gap-1 overflow-x-auto pb-2 gf-scroll">
            {frames.map((f, i) => (
              <button
                key={i}
                data-testid={`frame-${i}`}
                onClick={() => setCurrent(i)}
                className={
                  "relative shrink-0 w-14 h-14 border-2 transition-all " +
                  (i === current ? "border-cyan-400" : "border-zinc-800 hover:border-zinc-700 opacity-70 hover:opacity-100")
                }
                style={{ background: "#0a0a0c" }}
              >
                <FrameThumb pixels={f} />
                <span className="absolute -top-2 left-0 text-[9px] font-mono bg-zinc-950 text-zinc-400 px-1 border border-zinc-800">{i + 1}</span>
              </button>
            ))}
            <button
              data-testid="add-frame-btn"
              onClick={addFrame}
              className="shrink-0 w-14 h-14 border border-dashed border-zinc-700 hover:border-cyan-400 text-zinc-500 hover:text-cyan-400 flex items-center justify-center"
            >
              <Plus size={16} weight="bold" />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button data-testid="anim-duplicate-btn" onClick={duplicateFrame} variant="ghost" className="rounded-none text-zinc-200 border border-zinc-800 hover:bg-zinc-900 gap-2 h-8 text-xs">
              <Copy size={12} /> Duplicate
            </Button>
            <Button data-testid="anim-remove-btn" onClick={removeFrame} variant="ghost" className="rounded-none text-red-400 border border-zinc-800 hover:bg-zinc-900 gap-2 h-8 text-xs">
              <Trash size={12} /> Remove
            </Button>
            <div className="flex items-center gap-1 border border-zinc-800 bg-zinc-900 px-2 ml-2">
              <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">FPS</span>
              <input
                data-testid="anim-fps-input"
                type="number"
                min={1}
                max={60}
                value={fps}
                onChange={(e) => setFps(Math.max(1, Math.min(60, parseInt(e.target.value || "8"))))}
                className="bg-transparent w-10 text-sm font-mono text-zinc-100 outline-none px-1 py-1"
              />
            </div>
            {!playing ? (
              <Button data-testid="anim-play-btn" onClick={() => setPlaying(true)} className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold gap-2 h-8 text-xs">
                <Play size={12} weight="fill" /> Preview
              </Button>
            ) : (
              <Button data-testid="anim-stop-btn" onClick={() => setPlaying(false)} className="bg-yellow-400 hover:bg-yellow-300 text-zinc-950 rounded-none font-semibold gap-2 h-8 text-xs">
                <Pause size={12} weight="fill" /> Pause
              </Button>
            )}
          </div>
        </div>
      </div>

      <aside className="lg:col-span-4 space-y-4">
        <div className="border border-zinc-800 bg-zinc-900 p-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// PREVIEW</div>
          <canvas
            ref={previewRef}
            width={GRID * 4}
            height={GRID * 4}
            data-testid="anim-preview"
            style={{ imageRendering: "pixelated", width: "100%", height: "auto" }}
          />
        </div>

        <Input
          data-testid="anim-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Animation name"
          className="bg-zinc-900 border-zinc-800 rounded-none text-xs font-mono"
        />
        <Button data-testid="anim-save-btn" onClick={handleSave} className="w-full bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold tracking-wide gap-2">
          <FloppyDisk size={14} /> {t("ide.save")}
        </Button>

        {(() => {
          const grouped = {};
          (project.sprites || []).filter((s) => s.role === "frame" && s.data_url).forEach((s) => {
            const key = s.sheet_id || "frames";
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(s);
          });
          const sheets = Object.entries(grouped);
          if (!sheets.length) return null;
          return (
            <div className="space-y-2">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">// FRAMES IMPORTADOS</div>
              {sheets.map(([id, list]) => {
                const ordered = [...list].sort((a, b) => (a.frame_index || 0) - (b.frame_index || 0));
                return (
                  <div key={id} className="border border-zinc-800 bg-zinc-950 p-3 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-mono">{ordered[0]?.name?.replace(/-f\d+$/, "") || "sheet"}</div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{ordered.length} frames</div>
                    </div>
                    <button
                      onClick={() => {
                        setName(ordered[0]?.name?.replace(/-f\d+$/, "") || "sheet");
                        setFrames(ordered.map((s) => ({ data_url: s.data_url, w: s.width, h: s.height })));
                        setCurrent(0);
                        setPlaying(true);
                      }}
                      className="text-[10px] tracking-[0.25em] uppercase text-cyan-400 hover:text-cyan-300"
                    >
                      LOAD
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {(project.animations || []).length > 0 && (
          <div className="space-y-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">// SAVED</div>
            {(project.animations || []).map((a) => (
              <div key={a.id} className="border border-zinc-800 bg-zinc-950 p-3 flex items-center justify-between" data-testid={`saved-anim-${a.id}`}>
                <div>
                  <div className="text-xs font-mono">{a.name}</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{(a.frames || []).length} frames · {a.fps} FPS</div>
                </div>
                <button
                  onClick={() => { setName(a.name); setFps(a.fps); setFrames(a.frames); setCurrent(0); }}
                  className="text-[10px] tracking-[0.25em] uppercase text-cyan-400 hover:text-cyan-300"
                >
                  LOAD
                </button>
              </div>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}

function FrameThumb({ pixels }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0a0a0c";
    ctx.fillRect(0, 0, c.width, c.height);
    if (isImageFrame(pixels)) {
      const img = new Image();
      img.onload = () => {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0, c.width, c.height);
      };
      img.src = pixels.data_url;
      return;
    }
    if (!Array.isArray(pixels)) return;
    for (let y = 0; y < GRID; y++) {
      for (let x = 0; x < GRID; x++) {
        const c2 = pixels[y * GRID + x];
        if (c2) { ctx.fillStyle = c2; ctx.fillRect(x, y, 1, 1); }
      }
    }
  }, [pixels]);
  return <canvas ref={ref} width={GRID} height={GRID} style={{ imageRendering: "pixelated", width: "100%", height: "100%", display: "block" }} />;
}
